import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import './styles/mobile.css';

// Pages
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import AppMain from './pages/AppMain';
import SalesforceDocs from './pages/SalesforceDocs';
import PricingPage from './pages/PricingPage';
import LegalPage from './pages/LegalPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SharedAnalysisPage from './pages/SharedAnalysisPage';

// ScrollToTop — resets window scroll on every route change and pushes the
// main scroll container back to the top too. React Router preserves scroll
// position on navigation by default, which meant the dashboard was landing
// mid-page whenever the user jumped between screens. This component fixes
// that globally without changing any individual page's markup.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch { window.scrollTo(0, 0); }
    // Some screens render inside a <main> with its own overflow — reset
    // any element that has data-scroll-container="true" as well.
    document.querySelectorAll('[data-scroll-container="true"]').forEach(el => {
      try { el.scrollTop = 0; } catch { /* ignore */ }
    });
  }, [pathname]);
  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (location.state?.user) return children;

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#06080F", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 48, height: 48, border: "2px solid #2563EB33", borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Stash the requested deep-link so AppMain can resume it after login.
    // AuthPage navigates to /app on successful login; AppMain reads this key
    // on mount and consumes it (e.g. /app/analysis/<id> → opens that analysis).
    try {
      const target = location.pathname + (location.search || "");
      if (target && target.startsWith("/app/") && target !== "/app/") {
        sessionStorage.setItem("inference_post_login_redirect", target);
      }
    } catch (e) { /* ignore (private mode, etc.) */ }
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Public Route - redirects to app if already authenticated
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#06080F", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 48, height: 48, border: "2px solid #2563EB33", borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (isAuthenticated) return <Navigate to="/app" replace />;
  return children;
};

// Main Router
const AppRouter = () => {
  const location = useLocation();

  // ── SSO callback landing handler ──────────────────────────────────────
  // Both Microsoft (`/api/auth/microsoft/callback`) and Google
  // (`/api/auth/google/callback`) finish by redirecting the browser to
  // `${frontend}/app#access_token=…&refresh_token=…&provider=<provider>`.
  // We pull the tokens out of the URL hash, stash them where AuthContext
  // expects them, then reload so AuthContext bootstraps as if the user had
  // logged in normally. This hook MUST run before any early-return below
  // to satisfy React's rules-of-hooks.
  useEffect(() => {
    if (!location.hash?.includes('access_token=')) return;
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    const at = params.get('access_token');
    const rt = params.get('refresh_token');
    if (!at || !rt) return;
    try {
      localStorage.setItem('access_token', at);
      localStorage.setItem('refresh_token', rt);
    } catch (e) { /* private mode */ }
    window.history.replaceState(null, '', '/app');
    window.location.reload();
  }, [location.hash]);

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public read-only shared analysis page — no auth. */}
      <Route path="/shared/:token" element={<SharedAnalysisPage />} />

      {/* Dedicated marketing + legal routes */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/privacy" element={<LegalPage variant="privacy" title="Privacy Policy" testId="privacy-page" />} />
      <Route path="/terms" element={<LegalPage variant="terms" title="Terms of Service" testId="terms-page" />} />

      {/* Main App (Protected) — `/app` for the dashboard root, `/app/*` to
          catch authenticated deep-links (e.g. /app/analysis/:id). The
          ProtectedRoute auth guard runs for both; AppMain consumes the
          sub-path on mount to open the requested resource. */}
      <Route path="/app" element={<ProtectedRoute><AppMain /></ProtectedRoute>} />
      <Route path="/app/*" element={<ProtectedRoute><AppMain /></ProtectedRoute>} />

      {/* Public Docs */}
      <Route path="/docs/salesforce" element={<SalesforceDocs />} />

      {/* Legacy routes redirect */}
      <Route path="/dashboard" element={<Navigate to="/app" replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MaintenanceOverlay>
          <AppRouter />
        </MaintenanceOverlay>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#0D1117',
              border: '1px solid #1E2433',
              color: '#F9FAFB',
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
